import type { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { SUPPORT_EMAIL } from '../listingConflicts';

// [MCV-1889] Where a B2B listing is in its life. Filled is one-way: nothing un-fills a listing, so
// the only ways out of it are cancelling (a hand-filled listing, by the owner) or support ending
// its contract, and a cancelled listing can be relisted as a new draft.
export type ListingLifecycle =
  | 'CANCELLED'
  | 'FILLED_BY_CONTRACT'
  | 'FILLED'
  | 'ACTIVE'
  | 'INACTIVE';

type LifecycleListing = Pick<
  MsaasJobListing,
  'activeFlag' | 'filledFlag' | 'cancelledAt' | 'contractId'
>;

export const listingLifecycle = (listing: LifecycleListing): ListingLifecycle => {
  if (listing.cancelledAt) return 'CANCELLED';
  if (listing.filledFlag) return listing.contractId != null ? 'FILLED_BY_CONTRACT' : 'FILLED';
  return listing.activeFlag ? 'ACTIVE' : 'INACTIVE';
};

export const LIFECYCLE_LABEL: Record<ListingLifecycle, string> = {
  CANCELLED: 'Cancelled',
  FILLED_BY_CONTRACT: 'Filled',
  FILLED: 'Filled',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export type ListingStatusTab = 'all' | 'active' | 'filled' | 'cancelled';

export const STATUS_TAB_OPTIONS: Array<{ label: string; value: ListingStatusTab; cyTag: string }> =
  [
    { label: 'All', value: 'all', cyTag: 'msaas-status-tab-all' },
    { label: 'Active', value: 'active', cyTag: 'msaas-status-tab-active' },
    { label: 'Filled', value: 'filled', cyTag: 'msaas-status-tab-filled' },
    { label: 'Cancelled', value: 'cancelled', cyTag: 'msaas-status-tab-cancelled' },
  ];

export const matchesStatusTab = (listing: LifecycleListing, tab: ListingStatusTab): boolean => {
  const lifecycle = listingLifecycle(listing);
  switch (tab) {
    case 'active':
      return lifecycle === 'ACTIVE';
    case 'filled':
      return lifecycle === 'FILLED' || lifecycle === 'FILLED_BY_CONTRACT';
    case 'cancelled':
      return lifecycle === 'CANCELLED';
    default:
      return true;
  }
};

/** The mailto that asks support to cancel a filled listing's contract. */
export const supportCancelMailto = (
  listing: Pick<MsaasJobListing, 'id' | 'contractId'>
): string => {
  const subject = [
    `Cancel B2B listing #${listing.id}`,
    listing.contractId != null ? `contract #${listing.contractId}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return `mailto:${SUPPORT_EMAIL}?${new URLSearchParams({ subject }).toString().replace(/\+/g, '%20')}`;
};
